import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { groupBySector, type BrandListItem } from './api';

/**
 * Selettore brand raggruppato per verticale, come nell'Allegato A: un solo
 * verticale aperto alla volta, chiusura al click fuori, brand corrente marcato.
 */
interface BrandSelectorProps {
  brands: BrandListItem[];
  currentSlug: string | null;
  onSelect: (slug: string) => void;
  loading: boolean;
}

export function BrandSelector({
  brands,
  currentSlug,
  onSelect,
  loading,
}: BrandSelectorProps): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openSector, setOpenSector] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  const groups = groupBySector(brands);
  const current = brands.find((b) => b.slug === currentSlug) ?? null;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // All'apertura si mostra già espanso il verticale del brand corrente.
  useEffect(() => {
    if (open && current) setOpenSector(current.sectorKey);
  }, [open, current]);

  const label = loading
    ? t('brandSelector.loading')
    : (current?.name ?? t('shell.brandPlaceholder'));

  const disabled = !loading && brands.length === 0;

  return (
    <div className="brand-selector" ref={container}>
      <div
        className="brand-select"
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(disabled ? { 'aria-disabled': 'true' } : {})}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span>{disabled ? t('brandSelector.empty') : label}</span>
      </div>
      <div className="brand-select-arrow">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      <div className={open ? 'brand-dropdown open' : 'brand-dropdown'} role="listbox">
        {groups.map((group) => (
          <div
            key={group.sectorKey}
            className={
              openSector === group.sectorKey ? 'brand-dd-vertical open' : 'brand-dd-vertical'
            }
          >
            <div
              className="brand-dd-vertical-header"
              onClick={(e) => {
                e.stopPropagation();
                setOpenSector((v) => (v === group.sectorKey ? null : group.sectorKey));
              }}
            >
              <span>{group.sector}</span>
              <span className="dd-count">{group.brands.length}</span>
              <svg
                className="dd-chevron"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            <div className="brand-dd-brands">
              {group.brands.map((brand) => (
                <div
                  key={brand.slug}
                  className={
                    brand.slug === currentSlug ? 'brand-dd-brand active' : 'brand-dd-brand'
                  }
                  role="option"
                  aria-selected={brand.slug === currentSlug}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onSelect(brand.slug);
                  }}
                >
                  {brand.name}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
